/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-10-10
 */

import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { IUploadResourceResult } from '../component/solution/Interfaces';

interface LoginMessage {
    username: string;
    password: string;
}

export interface UserDetail {
    id: string,
    userName: string,
    firstName: string,
    lastName: string,
    email: string,
    cellphone: string,
    isAdmin: boolean;
    roles: string[];  
    userData: {
        stores: string[];  
    };
    admin: boolean;
    settingsAdmin: boolean
}

// Define the shape of the AuthContext
interface AuthContextType {
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<{ success: boolean }>;
    logout: () => void;
    fetchData: (endpoint: string, options: RequestInit, processData?: (result: any, status: string) => any, clean?: () => void) => void;
    fetchDataMulti: (endpoints: string[], options: RequestInit, processData?: (result?: any) => any, clean?: () => void) => void; 
    fetchDataMultiAdvanced: (
    endpoints: {
        endpoint: string,
        method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
        body?: any
    }[],
    processData?: (result?: any) => any,
    clean?: () => void
    ) => void;
    userDetail: UserDetail | null;
    token: string | null;
    responseErrorMessage: string | null;
    responseStatus: string | null;
    showErrorAlert: boolean; 
    handleCloseErrorAlert: () => void; 
    versionControl: VersionControlType | null;
    showOpenSettings: boolean;
    setShowOpenSettings: React.Dispatch<React.SetStateAction<boolean>>;
    authReady: boolean;
    setAuthReady: React.Dispatch<React.SetStateAction<boolean>>;
    uploadMultipleFiles: (files: File[], jsonPayload: Record<string, any>, url: string) => Promise<IUploadResourceResult[]>;
    setShowErrorAlert: React.Dispatch<React.SetStateAction<boolean>>;
    setResponseErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
    refreshTable: boolean;
    setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}

interface VersionControlType {
  pomVersion: string,
  buildCounter: number,
  buildDate: string
}

// interface RequestInit {
//     method?: string; // HTTP method like 'GET', 'POST', etc.
//     headers?: HeadersInit; // Headers or an object with key-value pairs
//     body?: BodyInit | null; // The request body, usually for POST or PUT requests
//     mode?: RequestMode; // CORS settings: 'cors', 'no-cors', 'same-origin'
//     credentials?: RequestCredentials; // Include credentials: 'include', 'same-origin', 'omit'
//     cache?: RequestCache; // Caching behavior: 'default', 'no-cache', 'reload', 'force-cache', etc.
//     redirect?: RequestRedirect; // Redirect behavior: 'follow', 'manual', 'error'
//     referrer?: string; // Referrer URL
//     referrerPolicy?: ReferrerPolicy; // Referrer policy like 'no-referrer', 'origin', etc.
//     integrity?: string; // Subresource integrity value
//     keepalive?: boolean; // Keeps the connection alive
//     signal?: AbortSignal | null; // Abort signal to cancel the request
//     window?: any; // Used for some old, non-standardized fetch API options
// }
  
interface Endpoint {
  endpoint: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: any;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [token, setToken] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [credentials, setCredentials] = useState<string>("");
    const [versionControl ,setVersionControl] = useState<VersionControlType | null>(null);
    const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
    const [responseErrorMessage, setResponseErrorMessage] = useState<string | null>(null);
    const [responseStatus, setResponseStatus]  = useState<string | null>(null);
    const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);   
    const [showOpenSettings, setShowOpenSettings] = useState<boolean>(false);   
    const [authReady, setAuthReady] = useState<boolean>(false); //Testing 

    const [refreshTable, setRefreshTable] = useState<boolean>(false); 

    useEffect(() => {
        
        try {
            const loggedInUrl: string = process.env.REACT_APP_LOGGED_IN_URL as string;
        
            fetchData(loggedInUrl, {method:"GET"}, (result: any) => {
                //console.log(`User Information`, result);
                setUserDetail(result);
            });
            
        } catch (error) {
            
        }
       
        //checking version
        const versionUrl: string = process.env.REACT_APP_VERSION_URL as string;
        fetchData(versionUrl, {method:"GET"}, (result: any) => {
            setVersionControl(result);
        });
          
    }, []);

    const handleCloseErrorAlert = () => {
        setShowErrorAlert(false);
    }

    // Login
    // const login = async (username: string, password: string) => {
        
    //     const loginUrl: string = process.env.REACT_APP_LOGIN_URL as string;

    //     setCredentials(btoa(`${username}:${password}`));
        
    //     const loginMessage: LoginMessage ={
    //         username: username,
    //         password: password,
    //     }

    //     fetchData(loginUrl, {method:"POST", body: JSON.stringify(loginMessage)}, (result: any) => {
    //         const token = result;
    //         setToken(token);
    //         localStorage.setItem("token", token);
    //         setIsAuthenticated(true);

    //         const loggedInUrl: string = process.env.REACT_APP_LOGGED_IN_URL as string;
    //         fetchData(loggedInUrl, {method:"GET"}, (result: any) => {
    //             console.log(`User Information`, result);
    //             setUserDetail(result);
    //             setIsAuthenticated(true);
    //             setShowOpenSettings(false);
    //         });

    //     });

    // };

    const login = async (username: string, password: string): Promise<{ success: boolean }> => {
    return new Promise((resolve) => {
        const loginUrl: string = process.env.REACT_APP_LOGIN_URL as string;

        setCredentials(btoa(`${username}:${password}`));

        const loginMessage: LoginMessage = {
        username,
        password,
        };

        fetchData(loginUrl, { method: "POST", body: JSON.stringify(loginMessage) }, (token: any) => {
        if (!token) return resolve({ success: false });

        setToken(token);
        localStorage.setItem("token", token);
        setIsAuthenticated(true);

        const loggedInUrl: string = process.env.REACT_APP_LOGGED_IN_URL as string;
        fetchData(loggedInUrl, { method: "GET" }, (userData: any) => {
            //console.log(`User Information`, userData);
            setUserDetail(userData);
            window.history.replaceState(null, "", "/");
            setIsAuthenticated(true);
            setAuthReady(true);
            setShowOpenSettings(false);
            resolve({ success: true });
        });
        });
    });
    };


    // Logout
    const logout = () => {
        setToken(null);
        setIsAuthenticated(false);
        setAuthReady(false);
        setUserDetail(null);
        setCredentials("");
        localStorage.removeItem("token");
        setShowOpenSettings(false);
    };

    // Fetch Data from a Single Endpoint
    const fetchData = async (   endpoint: string ,
                                options: RequestInit = {},
                                processData?: (result?: any, status?: any) => any,
                                clean?: () => void
                            ) => {
        
        const headers = new Headers(options.headers);
        if(! headers.has('Content-Type')) {
              headers.append('Content-Type', 'application/json');
        }else{
            if(headers.get("Content-Type") === "multipart/formdata"){
               headers.delete("Content-Type");
            } 
        }
        options.headers = headers;
        // console.log('Using Content-Type : ');
        // console.log(options.headers.get('Content-Type'));

        if(credentials.length > 0){
            options.headers.append("Authorization", `Basic ${credentials}`)
        } else {
            options.headers.append("Authorization", `Bearer ${localStorage.getItem("token")}`)
        }

        try {
            const response = await fetch(endpoint, options);

            if(response.status === 403) {
                //console.log("fectchData->logout()");
                logout();
            }
            setResponseErrorMessage(null);
            setResponseStatus(null);
            setShowErrorAlert(false);
            if (response.ok) {
                setIsAuthenticated(true);

                // console.log("response (OK):", response.status);
                
                const contentType = response.headers.get("Content-Type");
                let result: any = null; 

                if(contentType?.includes("application/json")){
            
                    result = await response.json();
                } else if (contentType?.includes("text/html") || contentType?.includes("text/plain")){
  
                    result = await response.text();
                } else {

                    result = await response.blob();
                }
                 
                if (processData) {
                    processData(result, response.status.toString());
                    console.log("fetchData->processData()");
                    setResponseErrorMessage(null); //resetting error response  message to null
                    setShowErrorAlert(false);
                    setResponseStatus(response.status.toString());
                } else {
                    console.log("fetchData");
                }
            } else {
                // console.log("response (NOT OKAY):", response.status);
                setResponseStatus(response.status.toString());
                const errorBody = await response.json(); 
                setResponseErrorMessage(`Response Status: ${response?.status} , Response Message: ${JSON.stringify(errorBody)}`); 
                setShowErrorAlert(true); //setting show error to true cause of error 
            }

        } catch (error) {
            console.error(error);
            setResponseErrorMessage(JSON.stringify(error)); 
            setShowErrorAlert(true);
        } finally {
            if(clean){
                clean();
            }
        }
    }   
 
    // Fetch Data from Multiple Endpoints
    const fetchDataMulti = async (  endpoints: string[],
                                    options: RequestInit = {},
                                    processData?: (result?: any) => any,
                                    clean?: () => void
                            ) => {
        
        const headers = new Headers(options.headers);
        if(! headers.has('Content-Type')) {
              headers.append('Content-Type', 'application/json');
        }else{
            if(headers.get("Content-Type") === "multipart/formdata"){
               headers.delete("Content-Type");
            } 
        }
        options.headers = headers;
        // console.log('Using Content-Type : ');
        // console.log(options.headers.get('Content-Type'));

        if(credentials.length > 0){
            options.headers.append("Authorization", `Basic ${credentials}`)
        } else {
            options.headers.append("Authorization", `Bearer ${localStorage.getItem("token")}`)
        }

        const loadedPromises: any[] = [];

        for(let ep in endpoints) {
            loadedPromises.push(
                fetch(endpoints[ep], options)
            );
        }

        try {
            const result = (
                await Promise.all(loadedPromises)
            ).map((response) => {
                const contentType = response.headers.get("Content-Type"); 
                if(contentType?.includes("application/json")){
                    return response.json();
                } else if (contentType?.includes("text/html") || contentType?.includes("text/plain")){
                    return response.text();
                } else {
                    return response.blob();
                }
            });

            const results: any[] = await Promise.all(result);

            if (processData) {
                processData(results);
                console.log("fetchData->processData()");
                setResponseErrorMessage(null); //resetting error response  message to null
                setShowErrorAlert(false);
            } else {
                console.log("fetchData");
            }
        } catch (error) {
            console.error(error);
            setResponseErrorMessage(JSON.stringify(error)); 
            setShowErrorAlert(true);
        } finally {
            if(clean){
                clean();
            }
        }
    } 


    //Testing new multi fetch 
    type Endpoint = {
        endpoint: string;
        method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" ;
        body?: any;
    };

    // Function definition
    const fetchDataMultiAdvanced = async (
        endpoints: Endpoint[],
        processData?: (result?: any) => any,
        clean?: () => void
        ): Promise<void> => {
        try {
            const loadedPromises = endpoints.map((ep: Endpoint) => {
                
            // Destructure *after* TypeScript knows `ep` is an Endpoint
            const { endpoint, method, body } = ep;

            const headers = new Headers();

            // Content-Type only if not FormData
            if (method !== "GET" && !(body instanceof FormData)) {
                headers.append("Content-Type", "application/json");
            }

            // Authorization
            if (credentials.length > 0) {
                headers.append("Authorization", `Basic ${credentials}`);
            } else {
                headers.append("Authorization", `Bearer ${localStorage.getItem("token")}`);
            }

            const reqOptions: RequestInit = {
                method,
                headers,
                body:
                body && method !== "GET"
                    ? body instanceof FormData
                    ? body
                    : JSON.stringify(body)
                    : undefined,
            };

           // console.log("Fetching:", endpoint, reqOptions);
            return fetch(endpoint, reqOptions);
            });

            const responses = await Promise.all(loadedPromises);

            const results = await Promise.all(
            responses.map(async (response) => {
                const contentType = response.headers.get("Content-Type");
                if (contentType?.includes("application/json")) return response.json();
                if (contentType?.includes("text/html") || contentType?.includes("text/plain")) return response.text();
                return response.blob();
            })
            );

            if (processData) {
            processData(results);
            setResponseErrorMessage(null);
            setShowErrorAlert(false);
            }
        } catch (error: any) {
            console.error("fetchDataMultiTesting error:", error);
            setResponseErrorMessage(JSON.stringify(error));
            setShowErrorAlert(true);
            throw error;
        } finally {
            if (clean) clean();
        }
    };






    //Uplaod Post Mutliple Files
    const uploadMultipleFiles = async (files: File[], jsonPayload: any, url: string): Promise<IUploadResourceResult[]> => {
        const results: IUploadResourceResult[] = [];

        for (const file of files) {         

            const formData = new FormData();
            formData.append("file", file);
            formData.append("json", new Blob([JSON.stringify(jsonPayload)], { type: "application/json" }));

            const headers = new Headers();
            //headers.append("Content-Type", "multipart/formdata"); 

            if (credentials.length > 0) {
               headers.append("Authorization", `Basic ${credentials}`);
            } else {
               headers.append("Authorization", `Bearer ${localStorage.getItem("token")}`);
            }

            try {
                const response = await fetch(url, { method: "POST", body: formData, headers: headers });  //

                if (response.ok) {
                    results.push({ fileName: file.name, success: true, status: response.status });
                } else {
                    const errorBody = await response.json().catch(() => null);
                    results.push({ fileName: file.name, success: false, status: response.status,error: errorBody });
                }
            } catch (err) {
                results.push({ fileName: file.name, success: false, status: 0, error: err });
            }
        }

        return results;
    }

    /*

    // Assume you have a <input type="file" id="file">
        const file = document.getElementById('file').files[0];

        const meta = { title: 'Report Q3', tags: ['finance', 'q3'] };

        // Build multipart/form-data
        const form = new FormData();
        form.append('file', file, file.name);
        // Send JSON as its own part with the proper MIME type
        form.append('meta', new Blob([JSON.stringify(meta)], { type: 'application/json' }), 'meta.json');

        const res = await fetch('/upload', {
        method: 'POST',
        body: form,              // IMPORTANT: don't set Content-Type yourself
        // headers: { 'Content-Type': 'multipart/form-data' } //  let the browser set boundary
        });


    */

    // Provide authentication context to the app
    return (
        <AuthContext.Provider value={{ 
            isAuthenticated, 
            login, 
            logout, 
            fetchData,
            fetchDataMulti,
            fetchDataMultiAdvanced,
            userDetail, 
            token, 
            responseErrorMessage,
            showErrorAlert,
            handleCloseErrorAlert,
            versionControl,
            responseStatus,
            showOpenSettings,
            setShowOpenSettings,
            authReady, 
            setAuthReady,
            uploadMultipleFiles, 
            setShowErrorAlert, 
            setResponseErrorMessage,
            refreshTable, 
            setRefreshTable
            }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
